/**
 * Fuente única para el rango de años de los selectores de CARGA de oportunidades.
 *
 * Regla: NO hardcodear años en cada componente. Todo selector de año de la capa de
 * carga (cotizador, formalización, carga manual, edición de cuotas) deriva su rango
 * de aquí.
 *
 * Rango = [y - 1, ..., y + 5] donde y = año actual.
 *  - Piso en y-1: mantiene el comportamiento de backdating (cargar el año anterior).
 *  - Techo en y+5: permite cargar oportunidades a futuro sin tocar código cada año.
 */
export const ANIOS_CARGA_OFFSET_PASADO = 1;
export const ANIOS_CARGA_OFFSET_FUTURO = 5;

/**
 * @returns {number[]} años disponibles para la capa de carga, ascendente.
 */
export const getAniosCarga = () => {
  const y = new Date().getFullYear();
  const anios = [];
  for (let a = y - ANIOS_CARGA_OFFSET_PASADO; a <= y + ANIOS_CARGA_OFFSET_FUTURO; a += 1) {
    anios.push(a);
  }
  return anios;
};

/** @returns {number} año mínimo seleccionable en la capa de carga (piso). */
export const getAnioCargaMin = () => getAniosCarga()[0];

/** @returns {number} año máximo seleccionable en la capa de carga (techo). */
export const getAnioCargaMax = () => {
  const anios = getAniosCarga();
  return anios.at(-1);
};

// ---------------------------------------------------------------------------
// Derivación de años desde los datos cargados (capa de VISUALIZACIÓN / filtros).
//
// Regla: cada lista sale de SU campo correcto, sin mezclar cierre con facturación.
//  - Año de CIERRE: sufijo "YYYY" de mes_cierre ("MM-YYYY"). ano_cierre está vacía
//    en DB, no se usa. Para contratos: año de mes_inicio + meses[].mes.
//  - Año de FACTURACIÓN: sufijo de mes_factura, o la columna `ano` (integer/string).
// Todo descarta null/malformados sin romper.
// ---------------------------------------------------------------------------

/** Extrae "YYYY" del sufijo de un "MM-YYYY". Devuelve null si no matchea. */
const anioDeMesAnio = (mesAnio) => {
  if (typeof mesAnio !== "string") return null;
  const m = mesAnio.match(/^\d{1,2}-(\d{4})$/);
  return m ? m[1] : null;
};

/** Normaliza un valor de año (number o string) a "YYYY". null si no es un año de 4 dígitos. */
const anioDeValor = (valor) => {
  if (valor == null) return null;
  const s = String(valor);
  return /^\d{4}$/.test(s) ? s : null;
};

const ordenarAniosDesc = (set) => [...set].sort((a, b) => Number(b) - Number(a));

/**
 * Años de CIERRE presentes en los datos.
 * @param {Array} oportunidades - filas con `mes_cierre` ("MM-YYYY"); incluir también BTL.
 * @param {Array} contratos - filas con `mes_inicio`/`meses[].mes` ("MM-YYYY") y/o `ano`.
 * @returns {string[]} años "YYYY" ordenados descendente.
 */
export const getAniosCierreDisponibles = (oportunidades = [], contratos = []) => {
  const set = new Set();
  for (const o of oportunidades) {
    const a = anioDeMesAnio(o?.mes_cierre);
    if (a) set.add(a);
  }
  for (const c of contratos) {
    const aInicio = anioDeMesAnio(c?.mes_inicio) || anioDeValor(c?.ano);
    if (aInicio) set.add(aInicio);
    for (const m of c?.meses || []) {
      const am = anioDeMesAnio(m?.mes);
      if (am) set.add(am);
    }
  }
  return ordenarAniosDesc(set);
};

/**
 * Años de FACTURACIÓN presentes en los datos.
 * Por fila prioriza el sufijo de `mes_factura`; si falta, cae a la columna `ano`.
 * @param {Array} oportunidades - filas con `mes_factura` y/o `ano`.
 * @returns {string[]} años "YYYY" ordenados descendente.
 */
export const getAniosFacturaDisponibles = (oportunidades = []) => {
  const set = new Set();
  for (const o of oportunidades) {
    const a = anioDeMesAnio(o?.mes_factura) || anioDeValor(o?.ano);
    if (a) set.add(a);
  }
  return ordenarAniosDesc(set);
};
